import queryString from "query-string";
import { getUserDeviceData } from "./userDeviceHelper";
import { useDeviceTypes } from "../hooks/useDeviceTypes";
import {
  TravelAppInterfaceEventPayload,
  TRAVEL_APP_INTERFACE_EVENT,
} from "redmond";
import { trackEvent } from "../api/trackEvent";
import { getConfigClientType } from "./getClientType";
import type { BookingCompleteData } from "./bookingCompleteTypes";

interface TravelAppWebInterfaceAndroid {
  sendTravelEvent: (data: string) => void;
}

interface TravelAppWebInterfaceIOS {
  postMessage: (data: string) => void;
}

declare global {
  interface Window {
    TravelAppWebInterface?: TravelAppWebInterfaceAndroid;
    webkit?: {
      messageHandlers: {
        TravelAppWebInterface?: TravelAppWebInterfaceIOS;
      };
    };
  }
}

export enum TravelAppInterfaceFunnelType {
  HOTELS = "hotels",
  PREMIUM_STAYS = "premiumStays",
  PACKAGES = "packages",
  FLIGHTS = "flights",
  CARS = "cars",
  ACTIVITIES = "activities",
  HOMES = "homes",
}

export enum TravelAppInterfaceEventName {
  DATES_FINALIZED = "datesFinalized",
  DATES_RESET = "datesReset",
  DATE_PICKED = "datePicked",
  BOOKING_COMPLETE = "bookingComplete",
  SESSION_INITIALIZED = "sessionInitialized",
  SESSION_EXPIRING = "sessionExpiring",
  SESSION_EXPIRED = "sessionExpired",
  SESSION_FAILED = "sessionFailed",
  CALENDAR_LOADED = "calendarLoaded",
  SEARCH_LOADED = "searchLoaded",
  NAVIGATE_TO_NATIVE_FEATURE = "navigateToNativeFeature",
  PAGE_LOADED = "pageLoaded",
  ANCILLARY_PURCHASE_COMPLETED = "ancillaryPurchaseCompleted",
  ANCILLARY_PURCHASE_FAILED = "ancillaryPurchaseFailed",
}

interface BaseEvent<N> {
  name: N;
  funnel?: TravelAppInterfaceFunnelType;
  data?: unknown;
}

interface BaseEventWithData<N, D> extends BaseEvent<N> {
  data: D;
}

type DatePickedEvent = BaseEventWithData<
  TravelAppInterfaceEventName.DATE_PICKED,
  { startDate?: string; endDate?: string }
>;

type DatesResetEvent = BaseEvent<TravelAppInterfaceEventName.DATES_RESET>;

type DatesFinalizedEvent =
  BaseEvent<TravelAppInterfaceEventName.DATES_FINALIZED>;

type BookingCompleteEvent = BaseEventWithData<
  TravelAppInterfaceEventName.BOOKING_COMPLETE,
  BookingCompleteData
>;

type SessionInitializedEvent =
  BaseEvent<TravelAppInterfaceEventName.SESSION_INITIALIZED>;

type SessionExpiringEvent =
  BaseEvent<TravelAppInterfaceEventName.SESSION_EXPIRING>;

type SessionExpiredEvent =
  BaseEvent<TravelAppInterfaceEventName.SESSION_EXPIRED>;

type SessionFailedEvent = BaseEvent<TravelAppInterfaceEventName.SESSION_FAILED>;

type CalendarLoadedEvent =
  BaseEvent<TravelAppInterfaceEventName.CALENDAR_LOADED>;

type SearchLoadedEvent = BaseEvent<TravelAppInterfaceEventName.SEARCH_LOADED>;

type NavigateToNativeFeatureEvent = BaseEventWithData<
  TravelAppInterfaceEventName.NAVIGATE_TO_NATIVE_FEATURE,
  {
    feature: {
      name: "book";
      function: {
        name: "search";
        data: { funnelType: TravelAppInterfaceFunnelType };
      };
    };
  }
>;

type PageLoadedEvent = BaseEvent<TravelAppInterfaceEventName.PAGE_LOADED>;

type AncillaryPurchaseCompletedEvent =
  BaseEvent<TravelAppInterfaceEventName.ANCILLARY_PURCHASE_COMPLETED>;

type AncillaryPurchaseFailedEvent =
  BaseEvent<TravelAppInterfaceEventName.ANCILLARY_PURCHASE_FAILED>;

type TravelAppEvent =
  | DatePickedEvent
  | DatesResetEvent
  | DatesFinalizedEvent
  | BookingCompleteEvent
  | SessionInitializedEvent
  | SessionExpiringEvent
  | SessionExpiredEvent
  | SessionFailedEvent
  | CalendarLoadedEvent
  | SearchLoadedEvent
  | NavigateToNativeFeatureEvent
  | PageLoadedEvent
  | AncillaryPurchaseCompletedEvent
  | AncillaryPurchaseFailedEvent;

const getIsAndroid = () => !!window.TravelAppWebInterface;

const getIsIOS = () => !!window.webkit?.messageHandlers.TravelAppWebInterface;

const sendTrackingLogs = (eventPayload: TravelAppInterfaceEventPayload) => {
  window.DD_LOGS?.logger.debug("sending travel app event", eventPayload);

  trackEvent({
    eventName: TRAVEL_APP_INTERFACE_EVENT,
    properties: eventPayload,
  });
};

const sendEvent = (event: TravelAppEvent) => {
  const eventPayload: TravelAppInterfaceEventPayload = {
    eventName: event.name,
    funnelType: event.funnel,
    eventData: event.data,
  };
  const stringifiedEvent = JSON.stringify(eventPayload);

  console.debug("sending travel app event", eventPayload);

  if (getIsAndroid()) {
    window.TravelAppWebInterface?.sendTravelEvent(stringifiedEvent);
    sendTrackingLogs(eventPayload);
  } else if (getIsIOS()) {
    window.webkit?.messageHandlers.TravelAppWebInterface?.postMessage(
      stringifiedEvent,
    );
    sendTrackingLogs(eventPayload);
  }
};

const travelAppInterface = { sendEvent };

// utility function to use outside of react components
const getIsTravelApp = (queryStringToParse?: string) => {
  if (getConfigClientType() === "app") {
    return { isTravelApp: true };
  }
  const params = queryString.parse(
    queryStringToParse || window.location.search,
  );
  const userDeviceData = getUserDeviceData();

  const isTravelApp =
    userDeviceData.deviceType === "mobile" &&
    (typeof params.isTravelApp === "string"
      ? params.isTravelApp === "true"
      : params.isTravelApp?.[0] === "true");

  return { isTravelApp };
};

// hook to use inside react components
const useIsTravelApp = () => {
  const params = queryString.parse(window.location.search);
  const { matchesMobile } = useDeviceTypes();

  const isTravelApp =
    matchesMobile &&
    (typeof params.isTravelApp === "string"
      ? params.isTravelApp === "true"
      : params.isTravelApp?.[0] === "true");

  return { isTravelApp };
};

/**
 * Helper function to append isTravelApp query parameter to a URL
 * @param url - The base URL to append the parameter to
 * @param isTravelApp - Whether to append the isTravelApp=true parameter
 * @returns The URL with isTravelApp parameter appended if isTravelApp is true
 */
const appendIsTravelAppParam = (url: string, isTravelApp: boolean): string => {
  return queryString.stringifyUrl({
    url,
    query: isTravelApp ? { isTravelApp: "true" } : {},
  });
};

export {
  travelAppInterface,
  useIsTravelApp,
  getIsTravelApp,
  appendIsTravelAppParam,
};
